class HeaderComponent extends HTMLElement {
  #lastScrollTop = 0;
  #timeout = null;
  #animationDelay = 150;
  #menuDrawerHiddenWidth = null;
  #offscreen = false;
  #intersectionObserver = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.setMetaTags();
    this.#injectStructuredData();
    this.#injectViewportAndLanguage();
    this.#injectGoogleVerification();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }

  setMetaTags() {
    const title = 'Trading Cards kaufen – Slabdealer.ch';
    const description = 'Entdecke Hobby Boxen, Rookies & Slabs im Schweizer Trading Card Shop. Jetzt online bestellen bei Slabdealer.ch.';
    const keywords = 'Trading Cards, Hobby Boxen, Rookies, Slabs, Schweiz, kaufen, Sportkarten';
    const imageUrl = 'https://slabdealer.ch/assets/og-image.jpg';
    const canonicalUrl = window.location.href;

    document.title = title;

    this.#setOrUpdateMeta('name', 'description', description);
    this.#setOrUpdateMeta('name', 'keywords', keywords);
    this.#setOrUpdateMeta('name', 'robots', 'index, follow');
    this.#setOrUpdateMeta('property', 'og:title', title);
    this.#setOrUpdateMeta('property', 'og:description', description);
    this.#setOrUpdateMeta('property', 'og:image', imageUrl);
    this.#setOrUpdateMeta('property', 'og:url', canonicalUrl);
    this.#setOrUpdateMeta('property', 'og:type', 'website');
    this.#setOrUpdateMeta('name', 'twitter:card', 'summary_large_image');
    this.#setOrUpdateMeta('name', 'twitter:title', title);
    this.#setOrUpdateMeta('name', 'twitter:description', description);
    this.#setOrUpdateMeta('name', 'twitter:image', imageUrl);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
  }

  #injectViewportAndLanguage() {
    this.#setOrUpdateMeta('name', 'viewport', 'width=device-width, initial-scale=1');
    this.#setOrUpdateMeta('http-equiv', 'Content-Language', 'de');
  }

  #injectGoogleVerification() {
    const verificationCode = 'DEIN_VERIFIZIERUNGSCODE'; // Ersetze mit echtem Code
    this.#setOrUpdateMeta('name', 'google-site-verification', verificationCode);
  }

  #injectStructuredData() {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Store",
      "name": "Slabdealer.ch",
      "url": "https://slabdealer.ch",
      "image": "https://slabdealer.ch/assets/logo.png",
      "description": "Trading Cards kaufen – Hobby Boxen, Rookies & Slabs im Schweizer Trading Card Shop.",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "CH"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  #setOrUpdateMeta(attrType, attrName, content) {
    let meta = document.querySelector(`meta[${attrType}="${attrName}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attrType, attrName);
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #observeStickyPosition(alwaysSticky = true) {
    if (this.#intersectionObserver) return;

    const config = { threshold: alwaysSticky ? 1 : 0 };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = isScrollingUp
        ? this.getBoundingClientRect().top >= 0 ? 'none' : 'up'
        : 'down';

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      calculateHeaderGroupHeight(header, headerGroup);
    });

    const observeChildren = () => {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element === header || !(element instanceof HTMLElement)) continue;
        resizeObserver.observe(element);
      }
    };

    observeChildren();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          observeChildren();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
